import { useCurrentUser } from '@pantry/api-client';
import { useSelectedHousehold } from '../contexts/HouseholdContext';
import {
  useDisplayLanguageCore,
  useResolveProductNameCore,
  useProductNameCore,
  useProductTranslationStatusCore,
} from '@pantry/shared-react/hooks';
import type { Product, ProductNames, LanguageCode } from '@pantry/shared';

/**
 * Hook to get the resolved display language for the current user/household.
 * Priority: user language > household language > country default > 'en'
 */
export function useDisplayLanguage(): LanguageCode {
  const { data: user } = useCurrentUser();
  const { selectedHousehold } = useSelectedHousehold();
  return useDisplayLanguageCore(user, selectedHousehold);
}

/**
 * Hook to resolve product names using the current user's language preference.
 * Returns a function that takes a product and returns the best available name.
 */
export function useResolveProductName() {
  const language = useDisplayLanguage();
  return useResolveProductNameCore(language);
}

/**
 * Get the resolved product name for a specific product.
 * @param product - The product to get the name for
 * @returns The product name in the user's preferred language
 */
export function useProductName(
  product: Product | { name: string; names?: ProductNames | null } | null | undefined
): string {
  const language = useDisplayLanguage();
  return useProductNameCore(product, language);
}

/**
 * Hook to get the translation status for a product.
 * Useful for showing translation contribution prompts.
 *
 * @param product - The product to check
 * @returns Translation status with canContribute, hasPreferredTranslation, etc.
 */
export function useProductTranslationStatus(product: Product | null | undefined) {
  const language = useDisplayLanguage();
  return useProductTranslationStatusCore(product, language);
}
